import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { colors, radii, shadows, spacing } from '../../../theme-v2';

export type DocumentPaperElevation = 'flat' | 'raised';

export interface DocumentPaperProps {
  /** The document body — PDF viewer, HTML content, markdown render, etc. */
  children: ReactNode;
  /**
   * Visual elevation. `raised` adds a soft shadow (default — typical
   * "sheet of paper" feel). `flat` is borderless + shadowless (for
   * embedded contexts where the experience page itself already has a
   * surface).
   */
  elevation?: DocumentPaperElevation;
  /**
   * When true, the paper grows to fill its parent and scrolls its
   * content internally. Defaults to true since the document area is
   * almost always the viewport's primary scroll region.
   */
  scrollable?: boolean;
  className?: string;
}

/**
 * DocumentPaper — white "sheet of paper" surface that wraps the
 * document content (PDF viewer / HTML body) inside the experience
 * page. Provides the visual reading frame: white bg, border, optional
 * shadow, internal padding, optional internal scroll.
 *
 * Stateless. Apps drop their viewer of choice inside as children;
 * DocumentPaper only owns the framing.
 */
export const DocumentPaper = ({
  children,
  elevation = 'raised',
  scrollable = true,
  className,
}: DocumentPaperProps) => (
  <StyledPaper
    $elevation={elevation}
    $scrollable={scrollable}
    className={className}
  >
    {children}
  </StyledPaper>
);

const raised = css`
  border: 1px solid ${colors.border.subtle};
  box-shadow: ${shadows.elev1};
`;

const flat = css`
  border: none;
  box-shadow: none;
`;

const StyledPaper = styled.div<{
  $elevation: DocumentPaperElevation;
  $scrollable: boolean;
}>`
  background: ${colors.surface.base};
  border-radius: ${radii.box}px;
  padding: ${spacing.lg}px;

  ${({ $elevation }) => ($elevation === 'raised' ? raised : flat)}

  ${({ $scrollable }) =>
    $scrollable &&
    css`
      height: 100%;
      overflow: auto;
    `}
`;
